import { Shield, ShieldCheck, ShieldAlert } from "lucide-react";

interface TrustScoreProps {
	score: number;
	size?: "sm" | "md" | "lg";
	showLabel?: boolean;
}

export function TrustScore({ score, size = "md", showLabel = true }: TrustScoreProps) {
	const getColor = () => {
		if (score >= 85) return "text-emerald-600";
		if (score >= 70) return "text-blue-600";
		if (score >= 50) return "text-yellow-600";
		return "text-red-600";
	};

	const getBgColor = () => {
		if (score >= 85) return "bg-emerald-50 border-emerald-200";
		if (score >= 70) return "bg-blue-50 border-blue-200";
		if (score >= 50) return "bg-yellow-50 border-yellow-200";
		return "bg-red-50 border-red-200";
	};

	const getLabel = () => {
		if (score >= 85) return "Trusted";
		if (score >= 70) return "Reliable";
		if (score >= 50) return "Building";
		return "New";
	};

	const getIcon = () => {
		if (score >= 85) return ShieldCheck;
		if (score >= 50) return Shield;
		return ShieldAlert;
	};

	const Icon = getIcon();

	const sizes = {
		sm: { icon: "w-3.5 h-3.5", text: "text-xs", badge: "px-1.5 py-0.5 gap-1" },
		md: { icon: "w-4 h-4", text: "text-sm", badge: "px-2 py-1 gap-1.5" },
		lg: { icon: "w-5 h-5", text: "text-base", badge: "px-3 py-1.5 gap-2" },
	};

	const s = sizes[size];

	return (
		<div
			className={`inline-flex items-center rounded-full border ${getBgColor()} ${s.badge}`}
		>
			<Icon className={`${s.icon} ${getColor()}`} />
			<span className={`font-semibold ${s.text} ${getColor()}`}>{score}</span>
			{showLabel && (
				<span className={`${s.text} text-muted-foreground`}>{getLabel()}</span>
			)}
		</div>
	);
}

interface TrustScoreBreakdownProps {
	score: number;
	totalLends: number;
	totalBorrows: number;
	responseRate: number;
	ratingCount: number;
}

export function TrustScoreBreakdown({
	score,
	totalLends,
	totalBorrows,
	responseRate,
	ratingCount,
}: TrustScoreBreakdownProps) {
	const factors = [
		{
			label: "Average Rating",
			value: `${ratingCount} reviews`,
			weight: "40%",
			bar: Math.min(100, ratingCount * 5),
		},
		{
			label: "Completion Rate",
			value: `${totalLends + totalBorrows} transactions`,
			weight: "25%",
			bar: Math.min(100, (totalLends + totalBorrows) * 4),
		},
		{
			label: "Damage-Free Record",
			value: "No issues reported",
			weight: "20%",
			bar: 95,
		},
		{
			label: "Response Rate",
			value: `${responseRate}%`,
			weight: "15%",
			bar: responseRate,
		},
	];

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<span className="text-sm font-medium">Trust Score Breakdown</span>
				<TrustScore score={score} size="sm" />
			</div>
			{factors.map((f) => (
				<div key={f.label} className="space-y-1">
					<div className="flex items-center justify-between text-xs">
						<span>{f.label}</span>
						<span className="text-muted-foreground">
							{f.value} ({f.weight})
						</span>
					</div>
					<div className="h-1.5 bg-muted rounded-full overflow-hidden">
						<div
							className="h-full bg-primary/70 rounded-full transition-all duration-500"
							style={{ width: `${f.bar}%` }}
						/>
					</div>
				</div>
			))}
		</div>
	);
}
